import { useState } from 'react'
import { createCustomerShipment, setSubscriptionPostnord } from '@/api/customers'
import { voidInvoice } from '@/api/invoices'
import { removeShipment, returnShipmentByCustomer, writeOffShipment } from '@/api/shipments'
import { useAuthStore } from '@/store/authStore'
import { getApiErrorMessage } from '@/lib/apiError'
import { formatSwedishDay } from '@/lib/format'
import { hasPermission } from '@/lib/permissions'
import {
  getCreateShipmentConfirmMessage,
  getCustomerReturnConfirmMessage,
  getPausedDeliveryNotice,
  getPlannedDeliveryText,
  getRemoveShipmentConfirmMessage,
  getWriteOffShipmentConfirmMessage
} from '@/lib/shipment'
import { getSubscriptionPauseNotice } from '@/lib/subscription'
import { returnShipmentWithInvoicePrompt, writeOffShipmentWithInvoicePrompt } from '@/lib/shipmentWriteOff'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { CustomerDeliveryRow } from '@/components/customer/CustomerDeliveryRow'
import type { ShipmentAction } from '@/lib/shipment'
import type { DeliveryPlan, Shipment, Subscription } from '@/api/types'

const ENABLE_CONFIRM_MESSAGE = 'Vill du aktivera leveranser för kunden? Riktiga PostNord-frakter bokas automatiskt.'
const TOGGLE_ERROR_MESSAGE = 'Kunde inte uppdatera leveransinställningen just nu.'
const CREATE_ERROR_MESSAGE = 'Kunde inte skapa leveransen just nu.'
const REMOVE_ERROR_MESSAGE = 'Kunde inte ta bort leveransen just nu.'
const WRITE_OFF_ERROR_MESSAGE = 'Kunde inte skriva av leveransen just nu.'
const CUSTOMER_RETURN_ERROR_MESSAGE = 'Kunde inte markera leveransen som returnerad just nu.'

function getAutomationText(isEnabled: boolean, isPaused: boolean): string {
  if (isPaused) return 'Pausat'

  return isEnabled ? 'Automatiska leveranser på' : 'Automatiska leveranser av'
}

interface CustomerDeliveriesCardProps {
  customerId: number
  subscription: Subscription | null
  shipments: Shipment[]
  deliveryPlan: DeliveryPlan | null
  isVerified: boolean
  onSubscriptionChanged: (subscription: Subscription) => void
  onDeliveriesChanged: () => Promise<void>
}

export function CustomerDeliveriesCard({
  customerId,
  subscription,
  shipments,
  deliveryPlan,
  isVerified,
  onSubscriptionChanged,
  onDeliveriesChanged
}: CustomerDeliveriesCardProps): React.ReactElement {
  const user = useAuthStore((state) => state.user)
  const canManageSubscriptions = hasPermission(user, 'subscriptions.manage')
  const canVoidInvoices = hasPermission(user, 'invoicing.void')
  const [isSaving, setIsSaving] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [noticeMessage, setNoticeMessage] = useState<string | null>(null)

  const sorted = [...shipments].sort((a, b) => b.created_at.localeCompare(a.created_at))
  const enabled = subscription?.enable_postnord === true
  const isPaused = subscription !== null && subscription.paused_at !== null
  const isBookedAutomatically = enabled && isVerified && !isPaused
  const nextShipment = deliveryPlan?.next ?? null
  const canCreateShipment = canManageSubscriptions && isBookedAutomatically && nextShipment !== null
  const canToggle = subscription !== null && canManageSubscriptions
  const subscriptionPauseNotice = subscription === null ? null : getSubscriptionPauseNotice(subscription)
  const planNotice = deliveryPlan === null ? null : getPausedDeliveryNotice(deliveryPlan)
  const pausedNotice = subscriptionPauseNotice ?? planNotice
  const plannedText =
    deliveryPlan === null || !isBookedAutomatically ? null : getPlannedDeliveryText(deliveryPlan, formatSwedishDay(new Date()))

  const count = sorted.length
  const hasShipments = count > 0
  const description = count === 0 ? 'Inga leveranser ännu' : `${count} ${count === 1 ? 'leverans' : 'leveranser'}`
  const automationText = getAutomationText(enabled, isPaused)
  const emptyText = enabled
    ? 'Första leveransen bokas automatiskt vid nästa körning – senast inom ett dygn.'
    : 'Aktivera så bokas kundens leveranser automatiskt via PostNord.'
  const toggleLabel = enabled ? 'Avaktivera leveranser' : 'Aktivera leveranser'
  const toggleVariant = enabled ? 'destructive' : 'default'

  const handleToggle = async (): Promise<void> => {
    if (!subscription) return

    if (!enabled && !window.confirm(ENABLE_CONFIRM_MESSAGE)) return

    setIsSaving(true)
    setErrorMessage(null)

    try {
      const updated = await setSubscriptionPostnord(customerId, !enabled)

      onSubscriptionChanged(updated)
    } catch {
      setErrorMessage(TOGGLE_ERROR_MESSAGE)
    } finally {
      setIsSaving(false)
    }
  }

  const runDeliveryChange = async (request: () => Promise<string | null>, failureMessage: string): Promise<void> => {
    setIsSaving(true)
    setErrorMessage(null)
    setNoticeMessage(null)

    try {
      setNoticeMessage(await request())
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error, failureMessage))
    }

    await onDeliveriesChanged()
    setIsSaving(false)
  }

  const createNextShipment = async (sequence: number): Promise<null> => {
    await createCustomerShipment(customerId, sequence)

    return null
  }

  const removeUnsentShipment = async (shipment: Shipment): Promise<null> => {
    await removeShipment(shipment.id)

    return null
  }

  const writeOffLostShipment = async (shipment: Shipment): Promise<string | null> => {
    const dependencies = { writeOff: writeOffShipment, voidInvoice, confirm: (message: string) => window.confirm(message) }
    const outcome = await writeOffShipmentWithInvoicePrompt(shipment.id, canVoidInvoices, dependencies)

    return outcome.notice
  }

  const returnShipmentFromCustomer = async (shipment: Shipment): Promise<string | null> => {
    const dependencies = { returnShipment: returnShipmentByCustomer, voidInvoice, confirm: (message: string) => window.confirm(message) }
    const outcome = await returnShipmentWithInvoicePrompt(shipment.id, canVoidInvoices, dependencies)

    return outcome.notice
  }

  const handleCreate = async (): Promise<void> => {
    if (!nextShipment) return

    if (!window.confirm(getCreateShipmentConfirmMessage(nextShipment, shipments))) return

    await runDeliveryChange(() => createNextShipment(nextShipment.sequence), CREATE_ERROR_MESSAGE)
  }

  const handleRemove = async (shipment: Shipment): Promise<void> => {
    if (!window.confirm(getRemoveShipmentConfirmMessage(shipment))) return

    await runDeliveryChange(() => removeUnsentShipment(shipment), REMOVE_ERROR_MESSAGE)
  }

  const handleWriteOff = async (shipment: Shipment): Promise<void> => {
    if (!window.confirm(getWriteOffShipmentConfirmMessage(shipment))) return

    await runDeliveryChange(() => writeOffLostShipment(shipment), WRITE_OFF_ERROR_MESSAGE)
  }

  const handleCustomerReturn = async (shipment: Shipment): Promise<void> => {
    if (!window.confirm(getCustomerReturnConfirmMessage(shipment))) return

    await runDeliveryChange(() => returnShipmentFromCustomer(shipment), CUSTOMER_RETURN_ERROR_MESSAGE)
  }

  const handleRowAction = (shipment: Shipment, action: ShipmentAction): void => {
    if (action === 'remove') {
      void handleRemove(shipment)
      return
    }

    if (action === 'customer_return') {
      void handleCustomerReturn(shipment)
      return
    }

    void handleWriteOff(shipment)
  }

  return (
    <Card className="shadow-none border-border/80">
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="space-y-1.5">
          <CardTitle>Leveranser</CardTitle>
          <CardDescription>
            {description} · {automationText}
          </CardDescription>
          {plannedText && <CardDescription>{plannedText}</CardDescription>}
        </div>
        <div className="flex shrink-0 flex-wrap justify-end gap-2">
          {canCreateShipment && (
            <Button
              variant="outline"
              className="rounded-none"
              disabled={isSaving}
              onClick={handleCreate}
            >
              Skapa leverans {nextShipment.sequence}
            </Button>
          )}
          {canToggle && (
            <Button
              variant={toggleVariant}
              className="rounded-none"
              disabled={isSaving}
              onClick={handleToggle}
            >
              {toggleLabel}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {pausedNotice && <p className="border border-amber-300 bg-amber-50/50 px-3 py-2 text-sm text-amber-900">{pausedNotice}</p>}

        {!hasShipments && <p className="text-sm text-muted-foreground">{emptyText}</p>}

        {hasShipments && (
          <ul className="max-h-72 divide-y divide-border/60 overflow-y-auto border border-border/70">
            {sorted.map((shipment) => (
              <CustomerDeliveryRow
                key={shipment.id}
                shipment={shipment}
                canManage={canManageSubscriptions}
                isDisabled={isSaving}
                onAction={handleRowAction}
              />
            ))}
          </ul>
        )}

        {noticeMessage && <p className="text-sm text-muted-foreground">{noticeMessage}</p>}

        {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}
      </CardContent>
    </Card>
  )
}
